/** Tour duration is stored as value + unit (`hours` | `days`). */

export type TourDurationUnit = 'hours' | 'days'

export function resolveDurationUnit(unit?: string | null): TourDurationUnit {
  return unit === 'days' ? 'days' : 'hours'
}

export function durationToHours(
  duration?: number | null,
  unit?: string | null,
): number {
  const d = Number(duration)
  if (!Number.isFinite(d) || d <= 0) return 24
  return resolveDurationUnit(unit) === 'days' ? d * 24 : d
}

export function durationHoursToCalendarDays(hours?: number | null): number {
  const h = Number(hours)
  if (!Number.isFinite(h) || h <= 0) return 1
  return Math.max(1, Math.ceil(h / 24))
}

export function durationToCalendarDays(
  duration?: number | null,
  unit?: string | null,
): number {
  if (resolveDurationUnit(unit) === 'days') {
    const d = Number(duration)
    if (!Number.isFinite(d) || d <= 0) return 1
    return Math.max(1, Math.round(d))
  }
  return durationHoursToCalendarDays(duration)
}

/** Convert displayed value when the agent switches the unit selector. */
export function convertDurationDisplayValue(
  value: number,
  from: TourDurationUnit,
  to: TourDurationUnit,
): number {
  if (!Number.isFinite(value) || value <= 0) return value
  if (from === to) return value
  if (from === 'hours' && to === 'days') {
    const days = value / 24
    return Number.isInteger(days) ? days : Math.round(days * 100) / 100
  }
  return value * 24
}

export type TourDurationLabels = {
  halfDay: string
  hour: string
  hours: string
  day: string
  days: string
}

export function formatTourDurationHours(
  hours: number | null | undefined,
  labels: TourDurationLabels,
): string {
  const h = Number(hours)
  if (!Number.isFinite(h) || h <= 0) return `—`

  if (h === 12) return labels.halfDay
  if (h < 24) return h === 1 ? `1 ${labels.hour}` : `${h} ${labels.hours}`

  const days = h / 24
  const daysLabel = Number.isInteger(days) ? days : Math.round(days * 10) / 10
  if (daysLabel === 1) return `1 ${labels.day}`
  return `${daysLabel} ${labels.days}`
}

export function formatTourDurationValue(
  duration: number | null | undefined,
  unit: string | null | undefined,
  labels: TourDurationLabels,
): string {
  if (resolveDurationUnit(unit) === 'days') {
    const d = Number(duration)
    if (!Number.isFinite(d) || d <= 0) return `—`
    return d === 1 ? `1 ${labels.day}` : `${d} ${labels.days}`
  }
  return formatTourDurationHours(duration, labels)
}
